import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { useExpert, IQAIRatings } from '../../contexts/ExpertContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import DynamicUIRenderer from '../../components/DynamicUIRenderer';
import { BrCard, BrButton, BrTextarea } from '@govbr-ds/react-components';

/**
 * Maps profile_id to accessibility_needs for CSS styling.
 * This allows generic CSS selectors like [data-accessibility*="low_vision"]
 * to work for any persona with those needs.
 */
function getAccessibilityNeedsFromProfile(profileId: string): string {
    const accessibilityMap: Record<string, string> = {
        'john_smith': 'low_vision',
        'dra_emily': 'low_vision_and_photophobia',
        'olivia': 'none',
        'dr_charles': 'none',
    };
    return accessibilityMap[profileId] || 'none';
}

// Modal de Salvamento
function SaveModal({
    isOpen,
    onClose,
    recoveryCode
}: {
    isOpen: boolean;
    onClose: () => void;
    recoveryCode: string | null;
}) {
    const { t } = useTranslation();
    const [codeCopied, setCodeCopied] = useState(false);

    if (!isOpen) return null;

    // Get current time for "last saved" display
    const now = new Date();
    const timeString = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    const handleCopyCode = async () => {
        try {
            await navigator.clipboard.writeText(recoveryCode || '');
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        } catch {
            // Fallback
            const textarea = document.createElement('textarea');
            textarea.value = recoveryCode || '';
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
            setCodeCopied(true);
            setTimeout(() => setCodeCopied(false), 2000);
        }
    };

    return (
        <div className="fixed inset-0 d-flex align-items-center justify-content-center z-50 p-4" style={{ backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
            <div style={{ maxWidth: '400px', width: '100%' }}>
                <BrCard>
                    <div className="p-4">
                        {/* Header */}
                        <div className="text-center mb-4">
                            <div className="d-inline-flex align-items-center justify-content-center bg-success rounded-circle mb-3" style={{ width: '64px', height: '64px', backgroundColor: 'rgba(22, 136, 33, 0.1)' }}>
                                <span className="text-3xl">✓</span>
                            </div>
                            <h2 className="text-xl text-weight-bold text-gray-80">{t('expert.saveModal.autoSavedTitle')}</h2>
                            <p className="text-gray-60 mt-1 text-sm">
                                {t('expert.saveModal.lastSavedAt', { time: timeString })}
                            </p>
                        </div>

                        {/* Código de Recuperação */}
                        <div className="bg-gray-2 rounded-lg p-3 mb-4 text-center border border-gray-10">
                            <p className="text-xs text-gray-60 text-uppercase text-weight-medium mb-1">{t('expert.saveModal.recoveryCode')}</p>
                            <p className="text-2xl text-family-mono text-weight-bold text-primary-default m-0" style={{ letterSpacing: '0.15em' }}>
                                {recoveryCode || '------'}
                            </p>
                        </div>

                        {/* Aviso importante */}
                        <div className="text-center mb-4 p-3 bg-gray-2 rounded-lg">
                            <p className="text-sm text-gray-80 m-0">
                                <strong>{t('expert.saveModal.important')}</strong> {t('expert.saveModal.autoRecoveryWarning')}
                            </p>
                        </div>

                        {/* Botões */}
                        <div className="d-flex flex-column gap-3">
                            <BrButton
                                onClick={handleCopyCode}
                                secondary
                                className="w-100"
                                aria-label={codeCopied ? t('expert.saveModal.copied') : t('expert.saveModal.copyCode')}
                            >
                                {codeCopied ? t('expert.saveModal.copied') : t('expert.saveModal.copyCode')}
                            </BrButton>
                            <BrButton
                                onClick={onClose}
                                primary
                                className="w-100"
                                aria-label={t('expert.saveModal.continueEvaluating')}
                            >
                                {t('expert.saveModal.continueEvaluating')}
                            </BrButton>
                        </div>
                    </div>
                </BrCard>
            </div>
        </div>
    );
}


export default function ExpertEvaluationPage() {
    const navigate = useNavigate();
    const { index } = useParams<{ index: string }>();
    const { language } = useLanguage();
    const { t } = useTranslation();
    const { state, getCurrentInterface, submitEvaluation, submitFinalEvaluation, isLastInterface, prevInterface, getProgress, getRecoveryCode, updateEvaluationDraft, getEvaluationDraft } = useExpert();

    const [ratings, setRatings] = useState<IQAIRatings>({
        clarity: 0,
        adequacy: 0,
        consistency: 0,
        accessibility: 0,
        usability: 0
    });
    const [comments, setComments] = useState('');
    const [errors, setErrors] = useState<string[]>([]);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [draftRestored, setDraftRestored] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const evaluationCardRef = useRef<HTMLDivElement>(null);

    const currentInterface = getCurrentInterface();
    const progress = getProgress();

    // Ref to track if we've initialized from draft for current interface
    const lastRestoredIndex = useRef<number | null>(null);

    // Restore evaluation or draft when page loads or interface changes
    useEffect(() => {
        if (!state.session) return;
        const currentIdx = state.session.current_index;
        const currentInterface = state.session.interfaces_to_evaluate[currentIdx];

        // Only restore once per interface
        if (lastRestoredIndex.current === currentIdx) return;

        // First, check if there's an already-submitted evaluation for this interface
        const existingEvaluation = state.session.evaluations.find(
            ev => ev.execution_id === currentInterface?.execution_id
        );

        if (existingEvaluation) {
            // Restore from previously submitted evaluation
            setRatings({
                clarity: existingEvaluation.ratings.clarity,
                adequacy: existingEvaluation.ratings.adequacy,
                consistency: existingEvaluation.ratings.consistency,
                accessibility: existingEvaluation.ratings.accessibility,
                usability: existingEvaluation.ratings.usability,
            });
            setComments(existingEvaluation.comments || '');
            setDraftRestored(true);
            console.log('Restored submitted evaluation for interface', currentIdx);
        } else {
            // Check for in-progress draft
            const draft = getEvaluationDraft();
            if (draft && draft.interface_index === currentIdx) {
                // Restore ratings from draft
                setRatings(prev => ({
                    clarity: draft.ratings.clarity ?? prev.clarity,
                    adequacy: draft.ratings.adequacy ?? prev.adequacy,
                    consistency: draft.ratings.consistency ?? prev.consistency,
                    accessibility: draft.ratings.accessibility ?? prev.accessibility,
                    usability: draft.ratings.usability ?? prev.usability,
                }));
                if (draft.comments !== undefined) {
                    setComments(draft.comments);
                }
                setDraftRestored(true);
                console.log('Restored evaluation draft for interface', currentIdx);
            } else {
                // Reset form for new interface
                setRatings({ clarity: 0, adequacy: 0, consistency: 0, accessibility: 0, usability: 0 });
                setComments('');
                setDraftRestored(false);
            }
        }
        lastRestoredIndex.current = currentIdx;
    }, [state.session?.current_index, getEvaluationDraft, state.session]);

    // Auto-save draft when ratings or comments change
    useEffect(() => {
        // Skip if no session or still loading
        if (!state.session || !state.initialized) return;

        // Skip the initial render and draft restoration
        if (!draftRestored && Object.values(ratings).every(v => v === 0) && comments === '') return;

        // Only save if there's something to save
        const hasRatings = Object.values(ratings).some(v => v > 0);
        const hasComments = comments.trim() !== '';

        if (hasRatings || hasComments) {
            updateEvaluationDraft(ratings, comments);
        }
    }, [ratings, comments, state.session, state.initialized, updateEvaluationDraft, draftRestored]);

    // Scroll to top when page loads or index changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, [index]);

    // Apply inert to #root when modal is open to trap focus for screen readers
    useEffect(() => {
        const root = document.getElementById('root');
        if (showPreviewModal && root) {
            root.setAttribute('inert', '');
            root.setAttribute('aria-hidden', 'true');
        } else if (root) {
            root.removeAttribute('inert');
            root.removeAttribute('aria-hidden');
        }
        return () => {
            if (root) {
                root.removeAttribute('inert');
                root.removeAttribute('aria-hidden');
            }
        };
    }, [showPreviewModal]);

    // Redirect if no session or invalid index
    useEffect(() => {
        // Wait for context to finish initialization before redirecting
        // This prevents race condition where session hasn't been restored from localStorage yet
        if (!state.initialized) {
            return;
        }

        if (!state.session) {
            navigate('/especialista');
            return;
        }

        // If we've completed all interfaces, go to summary
        if (state.session.current_index >= state.session.interfaces_to_evaluate.length) {
            navigate('/especialista/resumo');
            return;
        }

        const idx = parseInt(index || '0');
        if (idx !== state.session.current_index) {
            navigate(`/especialista/avaliacao/${state.session.current_index}`);
        }
    }, [state.session, state.initialized, index, navigate]);

    // Check if we should redirect to summary (all interfaces evaluated)
    // This happens synchronously BEFORE rendering loading state
    if (state.session && state.session.current_index >= state.session.interfaces_to_evaluate.length) {
        // Navigate to summary page since all interfaces are evaluated
        navigate('/especialista/resumo');
        return (
            <div className="d-flex align-items-center justify-content-center p-5">
                <div className="loading mr-2"></div>
                <p className="text-gray-60">{t('expert.completingEvaluation', 'Finalizando avaliação...')}</p>
            </div>
        );
    }

    if (!state.session || !currentInterface) {
        return (
            <div className="d-flex align-items-center justify-content-center p-5">
                <div className="loading mr-2"></div>
                <p className="text-gray-60">{t('expert.loading')}</p>
            </div>
        );
    }

    // Get translated persona info
    const profileId = currentInterface.profile_id || 'olivia';
    const personaName = t(`expert.personasPage.personas.${profileId}.name`, currentInterface.profile_id || 'Persona');
    const personaDescription = t(`expert.evaluationPage.personaDescriptions.${profileId}`, t('expert.evaluationPage.defaultPersonaDesc'));

    // Get translated task and phase labels
    const taskType = currentInterface.task_type || 'status_query';
    const taskLabel = t(`expert.evaluationPage.taskLabels.${taskType}`, currentInterface.task_type || t('expert.evaluationPage.task'));
    const taskDescription = t(`expert.evaluationPage.taskDescriptions.${taskType}`, '');
    const processPhase = currentInterface.process_phase || 'entry_tst';
    const phaseLabel = t(`expert.evaluationPage.phaseLabels.${processPhase}`, currentInterface.process_phase || t('expert.evaluationPage.phase'));

    const dimensions = [
        { key: 'clarity', label: t('expert.dimensions.clarity'), hint: t('expert.dimensions.clarityDesc') },
        { key: 'adequacy', label: t('expert.dimensions.adequacy'), hint: t('expert.dimensions.adequacyDesc') },
        { key: 'consistency', label: t('expert.dimensions.consistency'), hint: t('expert.dimensions.consistencyDesc') },
        { key: 'accessibility', label: t('expert.dimensions.accessibility'), hint: t('expert.dimensions.accessibilityDesc') },
        { key: 'usability', label: t('expert.dimensions.usability'), hint: t('expert.dimensions.usabilityDesc') }
    ];

    const handleRatingChange = (dimension: keyof IQAIRatings, value: number) => {
        setRatings(prev => ({ ...prev, [dimension]: value }));
        setErrors([]);
    };

    const validate = (): boolean => {
        const missing = dimensions.filter(d => ratings[d.key as keyof IQAIRatings] === 0);
        if (missing.length > 0) {
            setErrors(missing.map(d => d.label));
            // Scroll to evaluation card so user sees the error message
            evaluationCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (isSubmitting) return; // Prevent double-click
        if (!validate()) return;

        setIsSubmitting(true);

        try {
            // Determine next action BEFORE submitting (which increments index)
            const shouldNavigateToSummary = isLastInterface();
            const nextIndex = state.session!.current_index + 1;

            if (shouldNavigateToSummary) {
                // For the last evaluation, use submitFinalEvaluation which saves immediately
                // and marks the session as completed before navigating
                const success = await submitFinalEvaluation(ratings, comments);
                if (!success) {
                    // Show error - session save failed
                    setErrors([t('expert.evaluationPage.saveFailed') || 'Falha ao salvar. Tente novamente.']);
                    setIsSubmitting(false);
                    return;
                }
                // Reset form and navigate to summary
                setRatings({ clarity: 0, adequacy: 0, consistency: 0, accessibility: 0, usability: 0 });
                setComments('');
                navigate('/especialista/resumo');
            } else {
                // For intermediate evaluations, use regular submitEvaluation
                submitEvaluation(ratings, comments);
                // Reset form and navigate to next
                setRatings({ clarity: 0, adequacy: 0, consistency: 0, accessibility: 0, usability: 0 });
                setComments('');
                setIsSubmitting(false);
                navigate(`/especialista/avaliacao/${nextIndex}`);
            }
        } catch (error) {
            console.error('Error submitting evaluation:', error);
            setIsSubmitting(false);
        }
    };

    return (
        <div className="container-lg py-1 px-1 px-md-3">
            <div className="d-flex flex-column gap-2">
                {/* Save Modal */}
                <SaveModal
                    isOpen={showSaveModal}
                    onClose={() => setShowSaveModal(false)}
                    recoveryCode={getRecoveryCode()}
                />

                {/* Fullscreen Preview Modal - Rendered via Portal outside #root */}
                {showPreviewModal && createPortal(
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="preview-modal-title"
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 9999,
                            backgroundColor: getAccessibilityNeedsFromProfile(profileId).includes('photophobia') ? '#1a1a1a' : '#fff',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        {/* Modal Header */}
                        <div
                            className="d-flex align-items-center justify-content-between px-3"
                            style={{
                                backgroundColor: '#071D41',
                                color: 'white',
                                minHeight: '56px',
                                flexShrink: 0
                            }}
                        >
                            <div>
                                <div id="preview-modal-title" className="text-sm opacity-75">{t('expert.evaluationPage.interface')} {progress.current} {t('expert.evaluationPage.of')} {progress.total}</div>
                                <div className="text-weight-semi-bold" style={{ whiteSpace: 'nowrap' }}>{personaName}</div>
                            </div>
                            <BrButton
                                onClick={() => setShowPreviewModal(false)}
                                aria-label={t('expert.evaluationPage.close')}
                                autoFocus
                                style={{
                                    backgroundColor: 'white',
                                    color: '#071D41',
                                    padding: '8px 16px',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {t('expert.evaluationPage.close')}
                            </BrButton>
                        </div>

                        {/* Interface Content - Scrollable, fullscreen optimized, WITH ACCESSIBILITY ZOOM */}
                        <div
                            data-accessibility={getAccessibilityNeedsFromProfile(profileId)}
                            data-persona={profileId}
                            style={{
                                flex: 1,
                                overflow: 'auto',
                                backgroundColor: getAccessibilityNeedsFromProfile(profileId).includes('photophobia') ? '#1a1a1a' : '#fff'
                            }}
                        >
                            <DynamicUIRenderer
                                uiSpec={
                                    language === 'en' && currentInterface.ui_spec_en
                                        ? currentInterface.ui_spec_en
                                        : (currentInterface.ui_spec_pt || currentInterface.ui_spec)
                                }
                                fullscreen={true}
                                accessibilityNeeds={getAccessibilityNeedsFromProfile(profileId)}
                            />
                        </div>
                    </div>,
                    document.body
                )}

                {/* Main Content - Hidden from screen readers when modal is open */}
                {/* inert attribute completely disables focus and screen reader access */}
                <div
                    aria-hidden={showPreviewModal || showSaveModal}
                    {...((showPreviewModal || showSaveModal) ? { inert: '' } : {})}
                >
                    {/* Persona Context */}
                    <BrCard>
                        <div className="p-3">
                            <div className="d-flex flex-column md:flex-row md:align-items-start justify-content-between gap-3">
                                {/* Persona */}
                                <div className="flex-fill">
                                    <div className="text-xs text-gray-60 text-uppercase tracking-wide mb-1 font-weight-bold">{t('expert.evaluationPage.persona')}</div>
                                    <h2 className="text-xl text-weight-bold text-gray-80 m-0">{personaName}</h2>
                                    <p className="text-gray-60 text-sm mt-1">{personaDescription}</p>
                                </div>

                                {/* Tarefa e Fase */}
                                <div className="d-flex flex-column gap-2 md:text-right">
                                    <div>
                                        <span className="text-xs text-gray-60 text-uppercase tracking-wide font-weight-bold">{t('expert.evaluationPage.task')}</span>
                                        <p className="text-weight-semi-bold text-gray-80 m-0">{taskLabel}</p>
                                    </div>
                                    <div>
                                        <span className="text-xs text-gray-60 text-uppercase tracking-wide font-weight-bold">{t('expert.evaluationPage.phase')}</span>
                                        <p className="text-weight-medium text-gray-70 m-0">{phaseLabel}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Descrição da Tarefa */}
                            {taskDescription && (
                                <div className="mt-3 pt-3 border-top border-gray-10">
                                    <p className="text-sm text-gray-60 font-italic m-0">
                                        📋 {taskDescription}
                                    </p>
                                </div>
                            )}

                            {/* Button to open fullscreen preview - works on all screens */}
                            <div className="mt-4 pt-3 border-top border-gray-10">
                                <BrButton
                                    primary
                                    onClick={() => setShowPreviewModal(true)}
                                    className="w-100 text-break"
                                    aria-label={t('expert.evaluationPage.viewInterfaceBtn')}
                                >
                                    {t('expert.evaluationPage.viewInterfaceBtn')}
                                </BrButton>
                                <p className="text-center text-xs text-gray-50 mt-2 mb-0">
                                    {t('expert.evaluationPage.viewInterfaceHint')}
                                </p>
                            </div>
                        </div>
                    </BrCard>

                    {/* Evaluation Form - Direct display without tabs */}
                    <div ref={evaluationCardRef}>
                        <BrCard>
                            <div className="p-3">
                                <h3 className="text-lg text-weight-semi-bold text-gray-80 mb-4">
                                    {t('expert.evaluationPage.iqaiTitle', { current: progress.current, total: progress.total })}
                                </h3>


                                {errors.length > 0 && (
                                    <div className="bg-warning-light border border-warning rounded-lg p-2 mb-3 text-center" style={{ backgroundColor: 'rgba(255, 193, 7, 0.1)' }}>
                                        <p className="text-gray-80 m-0 text-sm">
                                            ⚠️ {t('expert.evaluationPage.missingRatings', { count: errors.length })}
                                        </p>
                                    </div>
                                )}

                                <div className="d-flex flex-column gap-3">
                                    {dimensions.map((dim) => (
                                        <div key={dim.key} className="border-bottom border-gray-5 pb-4 last:border-0">
                                            <div className="d-flex flex-column sm:flex-row sm:justify-content-between sm:align-items-center mb-3">
                                                <div>
                                                    <label className="text-weight-bold text-gray-80 d-block">{dim.label}</label>
                                                    <span className="text-sm text-gray-60">{dim.hint}</span>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-1">
                                                {[1, 2, 3, 4, 5].map((value) => {
                                                    const isSelected = ratings[dim.key as keyof IQAIRatings] === value;
                                                    return (
                                                        <button
                                                            key={value}
                                                            type="button"
                                                            onClick={() => handleRatingChange(dim.key as keyof IQAIRatings, value)}
                                                            className={`flex-fill py-3 rounded-md font-weight-medium transition-colors border-0 ${isSelected
                                                                ? 'bg-primary-default text-pure-0 shadow-sm'
                                                                : 'bg-gray-5 text-gray-60 hover-bg-gray-10'
                                                                }`}
                                                            style={{ minHeight: '40px', transition: 'all 0.2s' }}
                                                        >
                                                            {value}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                            <div className="d-flex justify-content-between text-xs text-gray-50 mt-2 px-1">
                                                <span>{t('expert.scale.veryBad')}</span>
                                                <span>{t('expert.scale.excellent')}</span>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Comments */}
                                    <div className="mt-2">
                                        <BrTextarea
                                            id="expert-comments"
                                            label={t('expert.evaluationPage.comments')}
                                            value={comments}
                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setComments(e.target.value)}
                                            placeholder={t('expert.evaluationPage.commentsPlaceholder')}
                                        />
                                    </div>
                                </div>

                                {/* Botões */}
                                <div className="mt-6 d-flex flex-column gap-3">
                                    <div className="d-flex flex-wrap gap-3">
                                        {state.session && state.session.current_index > 0 && (
                                            <BrButton
                                                secondary
                                                onClick={prevInterface}
                                                className="flex-fill text-break"
                                                aria-label={t('expert.evaluationPage.back')}
                                            >
                                                {t('expert.evaluationPage.back')}
                                            </BrButton>
                                        )}
                                        <BrButton
                                            primary
                                            onClick={handleSubmit}
                                            className="flex-fill text-break"
                                            aria-label={isLastInterface() ? t('expert.evaluationPage.finish') : t('expert.evaluationPage.next')}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <span className="loading mr-2" style={{ width: '16px', height: '16px' }} aria-hidden="true"></span>
                                                    {t('common.sending') || 'Enviando...'}
                                                </>
                                            ) : (
                                                isLastInterface() ? t('expert.evaluationPage.finish') : t('expert.evaluationPage.next')
                                            )}
                                        </BrButton>
                                    </div>

                                    <BrButton
                                        secondary
                                        onClick={() => setShowSaveModal(true)}
                                        className="w-100 text-break"
                                        aria-label={t('expert.evaluationPage.save')}
                                    >
                                        <i className="fas fa-save mr-2" aria-hidden="true"></i> {t('expert.evaluationPage.save')}
                                    </BrButton>
                                </div>
                            </div>
                        </BrCard>
                    </div>
                </div>
            </div>
        </div>
    );
}
